/**
 * Shared utility functions for Ctrl Enter application
 */

export { randomUUID } from './random-id';

/**
 * Get base64 string size in KB
 * @param base64 The base64 string to measure
 * @returns Size in KB
 */
export const getBase64SizeKB = (base64: string): number => {
  // Remove data URL prefix if present
  const base64Data = base64.includes(',') ? base64.split(',')[1] || base64 : base64;
  // Base64 encoding increases size by ~33%, so actual size is roughly length * 0.75
  return Math.round((base64Data.length * 0.75) / 1024);
};

/**
 * Calculate total size of attachments in KB
 * @param attachments Array of attachments with url property
 * @returns Total size in KB
 */
export const calculateAttachmentsSize = (attachments: Array<{ url: string }>): number => {
  return attachments.reduce((total, attachment) => {
    return total + getBase64SizeKB(attachment.url);
  }, 0);
};

/**
 * Convert bytes to human-readable format
 * @param bytes Number of bytes
 * @param decimals Number of decimal places (default: 2)
 * @returns Human-readable string (e.g., "1.23 MB")
 */
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Convert KB to human-readable format
 * @param kb Number of kilobytes
 * @param decimals Number of decimal places (default: 1)
 * @returns Human-readable string (e.g., "1.2 MB")
 */
export const formatKB = (kb: number, decimals = 1): string => {
  return formatBytes(kb * 1024, decimals);
};

/**
 * Validate if a file is a supported format (generic function)
 * @param file The file to validate
 * @param supportedTypes Array of supported MIME types
 * @param maxSize Maximum file size in bytes
 * @returns Validation result with success flag and optional error message
 */
export const validateFile = (
  file: File,
  supportedTypes: readonly string[],
  maxSize: number
): { valid: boolean; error?: string } => {
  // Check file type
  if (!supportedTypes.includes(file.type)) {
    const supportedFormats = supportedTypes
      .map(type => {
        if (type.startsWith('image/')) {
          return type.replace('image/', '');
        } else if (type === 'application/pdf') {
          return 'pdf';
        } else {
          return type;
        }
      })
      .join(', ');
    return {
      valid: false,
      error: `Unsupported file type: ${file.type}. Supported formats: ${supportedFormats}`,
    };
  }

  // Check file size
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File too large: ${fileSizeMB}MB. Maximum size: ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
};

/**
 * Validate if a file is a supported image format
 * @param file The file to validate
 * @param supportedTypes Array of supported MIME types
 * @param maxSize Maximum file size in bytes
 * @returns Validation result with success flag and optional error message
 * @deprecated Use validateFile for mixed file type support
 */
export const validateImageFile = (
  file: File,
  supportedTypes: readonly string[],
  maxSize: number
): { valid: boolean; error?: string } => {
  return validateFile(file, supportedTypes, maxSize);
};

/**
 * Safe deep clone utility - uses structuredClone with fallback to JSON round-trip
 * @param obj Object to clone
 * @returns Deep cloned object
 */
export const safeDeepClone = <T>(obj: T): T => {
  if (typeof structuredClone !== 'undefined') {
    return structuredClone(obj);
  }
  // Fallback for older environments
  return JSON.parse(JSON.stringify(obj)) as T;
};

/**
 * Debounce function - delays execution until after wait milliseconds have elapsed
 * @param func Function to debounce
 * @param wait Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Throttle function - ensures function is called at most once per wait milliseconds
 * @param func Function to throttle
 * @param wait Wait time in milliseconds
 * @returns Throttled function
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let lastTime = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastTime >= wait) {
      lastTime = now;
      func(...args);
    }
  };
};

/**
 * Generate a random ID string
 * @param length Length of the ID (default: 8)
 * @returns Random ID string
 */
export const generateId = (length = 8): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Generate a simple random ID using Math.random and timestamp (alternative implementation)
 * @returns Random ID string
 */
export const generateSimpleId = (): string => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * Check if a value is a valid URL
 * @param value String to check
 * @returns True if valid URL, false otherwise
 */
export const isValidUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Trim message to maximum length
 * @param message Message to trim
 * @param maxLength Maximum length (default: 20000)
 * @returns Trimmed message
 */
export const trimMessage = (message: string, maxLength = 20000): string => {
  return message.length <= maxLength ? message : message.slice(0, maxLength);
};

/**
 * Sanitize text for API calls by removing invalid Unicode characters
 * Removes lone surrogate pairs, null bytes, and other problematic characters
 * that can break JSON parsing or API requests
 *
 * @param text Text to sanitize
 * @returns Sanitized text safe for API calls
 *
 * @example
 * sanitizeTextForAPI("Hello\ud83c World") // "Hello World" (removes lone surrogate)
 * sanitizeTextForAPI("Test\x00Text") // "TestText" (removes null bytes)
 */
export const sanitizeTextForAPI = (text: string): string => {
  if (!text) return text;

  return (
    text
      // Remove lone surrogate pairs (U+D800 to U+DFFF)
      // These are half of emoji pairs that break JSON parsing
      .replace(/[\uD800-\uDFFF]/g, '')
      // Remove null bytes and other control characters (except newlines, tabs, carriage returns)
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      // Normalize Unicode to NFC form (combines decomposed characters)
      .normalize('NFC')
      // Trim whitespace
      .trim()
  );
};

/**
 * Validate email format
 * @param email Email string to validate
 * @returns True if valid email format, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Export logger utilities
export * from './logger';

// Note: Document extraction exports moved to separate entry point to avoid bundling pdf-parse
// Server should import from '@ctrl-enter/tools/utils/document-extraction' directly
// This prevents Electron main process from trying to bundle/require pdf-parse
// Note: File transport exports moved to separate entry point to avoid Node.js dependency issues
// Import directly from '@ctrl-enter/tools/utils/file-transport' when needed
